import React, { forwardRef, type ForwardedRef } from 'react';
import { clsx } from 'clsx';
import './DatePicker.scss';

export interface ESSDatePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Label for the date picker */
  label?: React.ReactNode;
  /** Helper text to display below the date picker */
  helperText?: string;
  /** Whether the date picker is in an error state */
  isInvalid?: boolean;
  /** Error message to display when isInvalid is true */
  errorMessage?: string;
  /** Size of the date picker */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * ESS DatePicker Component - Simplified version
 * Note: Uses native HTML5 date input. For advanced date picking,
 * consider integrating a library like react-datepicker.
 */
export const DatePicker = forwardRef<HTMLInputElement, ESSDatePickerProps>(
  function DatePicker(
    {
      label,
      helperText,
      isInvalid,
      errorMessage,
      size = 'md',
      className,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const datePickerClasses = clsx(
      'ess-datepicker',
      `ess-datepicker--${size}`,
      {
        'ess-datepicker--invalid': isInvalid,
      },
      className,
    );

    return (
      <div className={datePickerClasses}>
        {label && <label className="ess-datepicker__label">{label}</label>}
        <input
          ref={ref}
          type="date"
          className="ess-datepicker__input"
          aria-invalid={isInvalid}
          {...props}
        />
        {(helperText || (isInvalid && errorMessage)) && (
          <div className="ess-datepicker__help-text">
            {isInvalid && errorMessage ? errorMessage : helperText}
          </div>
        )}
      </div>
    );
  },
);

DatePicker.displayName = 'ESSDatePicker';
